import type { KeyboardEvent } from 'react';
import Tooltip from '@mui/material/Tooltip';
import {
    GridEditLongTextCell,
    type GridRenderEditCellParams
} from '../lib/dataGrid';

/**
 * Enter belongs to the grid (save), never to the textarea. The grid drops an
 * Enter that comes while a typed value is still on its way to it, and the
 * textarea would then type the line break instead — a stray empty line in the
 * record, where an ordinary cell simply ignores the key.
 */
function keepEnterOutOfTheText(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
    }
}

/**
 * Grid edit cell for text the form writes over several lines. A plain cell
 * input is an `<input>`, which drops line breaks — opening and leaving such a
 * cell would rewrite the record without them. MUI's long-text editor opens a
 * textarea below the cell instead: Enter saves like in every other cell,
 * Shift+Enter starts a new line.
 *
 * Wrapped to surface the column's validation error, as the other cells do, and
 * to hand every keystroke on at once (`debounceMs`), so an Enter straight after
 * typing saves rather than being dropped.
 */
export default function GridMultilineEditCell(
    props: GridRenderEditCellParams & { error?: string | null }
) {
    const { error, ...rest } = props;
    return (
        <Tooltip
            open={!!error && props.hasFocus}
            title={error ?? ''}
            arrow
            placement="top"
        >
            <span
                style={{ display: 'flex', width: '100%', height: '100%' }}
                onKeyDownCapture={keepEnterOutOfTheText}
            >
                <GridEditLongTextCell {...rest} debounceMs={0} />
            </span>
        </Tooltip>
    );
}
